import { http } from './http'

const unwrap = (body) => body?.data ?? null

/** CRUD standard : list renvoie {data, links, meta}, les autres renvoient la ressource. */
function resource(base) {
  return {
    list: (params, options) => http.get(base, { ...options, params }),
    get: (id, options) => http.get(`${base}/${id}`, options).then(unwrap),
    create: (body) => http.post(base, body).then(unwrap),
    update: (id, body) => http.put(`${base}/${id}`, body).then(unwrap),
    remove: (id) => http.delete(`${base}/${id}`)
  }
}

export const api = {
  auth: {
    login: (body) => http.post('/auth/login', body, { auth: false }).then(unwrap),
    register: (body) => http.post('/auth/register', body, { auth: false }).then(unwrap),
    forgotPassword: (body) => http.post('/auth/password/forgot', body, { auth: false }),
    resetPassword: (body) => http.post('/auth/password/reset', body, { auth: false }),
    me: () => http.get('/auth/me').then(unwrap),
    logout: () => http.post('/auth/logout'),
    logoutAll: () => http.post('/auth/logout-all'),
    changePassword: (body) => http.post('/auth/password/change', body)
  },
  users: resource('/users'),
  roles: { list: () => http.get('/roles').then(unwrap) },
  owners: {
    ...resource('/owners'),
    // Ouvre un accès (compte de connexion) sur une fiche existante : 201 avec la fiche et son bloc account.
    openAccount: (id, body) => http.post(`/owners/${id}/account`, body).then(unwrap)
  },
  drivers: {
    ...resource('/drivers'),
    // Ouvre un accès (compte de connexion, rôle chauffeur) sur une fiche existante.
    openAccount: (id, body) => http.post(`/drivers/${id}/account`, body).then(unwrap),
    history: (id, params) => http.get(`/drivers/${id}/history`, { params }),
    assign: (id, body) => http.post(`/drivers/${id}/assignments`, body).then(unwrap),
    endAssignment: (id, assignmentId, body) =>
      http.post(`/drivers/${id}/assignments/${assignmentId}/end`, body || {}).then(unwrap)
  },
  vehicleTypes: resource('/vehicle-types'),
  vehicles: {
    ...resource('/vehicles'),
    driver: (id) => http.get(`/vehicles/${id}/driver`).then(unwrap),
    fuelConsumption: (id, params) => http.get(`/vehicles/${id}/fuel-consumption`, { params }).then(unwrap)
  },
  revenues: {
    ...resource('/revenues'),
    summary: (params) => http.get('/revenues/summary', { params }).then(unwrap)
  },
  expenseCategories: resource('/expense-categories'),
  expenses: {
    ...resource('/expenses'),
    uploadReceipt(id, file) {
      const form = new FormData()
      form.append('receipt', file)
      return http.upload(`/expenses/${id}/receipt`, form).then(unwrap)
    },
    receipt: (id) => http.blob(`/expenses/${id}/receipt`),
    removeReceipt: (id) => http.delete(`/expenses/${id}/receipt`).then(unwrap)
  },
  reconciliation: (params) => http.get('/reconciliation', { params }).then(unwrap),
  fuelLogs: resource('/fuel-logs'),
  maintenancePlans: resource('/maintenance-plans'),
  maintenanceRecords: resource('/maintenance-records'),
  maintenanceDue: (params) => http.get('/maintenance/due', { params }).then(unwrap),
  dashboard: {
    summary: (params) => http.get('/dashboard/summary', { params }).then(unwrap),
    trends: (params) => http.get('/dashboard/trends', { params }).then(unwrap),
    topVehicles: (params) => http.get('/dashboard/top-vehicles', { params }).then(unwrap)
  },
  reports: {
    ...resource('/reports'),
    download: (id) => http.blob(`/reports/${id}/download`)
  },
  notifications: {
    list: (params, options) => http.get('/notifications', { ...options, params }),
    unreadCount: () => http.get('/notifications/unread-count').then((b) => b?.data?.unread ?? 0),
    markRead: (id) => http.post(`/notifications/${id}/read`).then(unwrap),
    markAllRead: () => http.post('/notifications/read-all').then(unwrap),
    remove: (id) => http.delete(`/notifications/${id}`)
  },
  // Configuration du temps réel (optionnel) : { enabled, connection, channels }.
  realtime: { config: () => http.get('/realtime/config').then(unwrap) },
  // Calcule ce que touche chaque chauffeur sur la période (pourcentage × base, voir config/resources.js).
  salaries: { compute: (params, options) => http.get('/salaries', { ...options, params }).then(unwrap) },
  auditLogs: { list: (params, options) => http.get('/audit-logs', { ...options, params }) },
  payments: {
    ...resource('/payments'),
    validate: (id) => http.post(`/payments/${id}/validate`).then(unwrap),
    reject: (id, reason) => http.post(`/payments/${id}/reject`, { reason }).then(unwrap),
    /**
     * Paiements groupés par date ({ '2026-09-20': [ { id, amount, method, status, vehicle, driver, reference } ] }).
     * `params` accepte `from`, `to` et les filtres `filter[status]`, `filter[vehicle_id]`,
     * `filter[driver_id]` (l'objet `filter` est converti par buildQuery) ainsi qu'un
     * `signal` pour annuler une période devenue obsolète.
     * La réponse est acceptée avec ou sans enveloppe { data: … }.
     */
    calendar: (params, options) =>
      http.get('/payments/calendar', { ...options, params }).then((body) => unwrap(body) ?? body ?? {})
  }
}

/**
 * Charge toutes les pages d'une liste (listes de référence des menus
 * déroulants) : 100 éléments par page, 10 pages au maximum.
 */
export async function fetchAll(list, params = {}, maxPages = 10) {
  const first = await list({ ...params, per_page: 100, page: 1 })
  const items = [...first.data]
  const last = first.meta?.last_page || 1
  for (let page = 2; page <= Math.min(last, maxPages); page++) {
    const next = await list({ ...params, per_page: 100, page })
    items.push(...next.data)
  }
  return items
}
